import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Analytics,
  CheckCircle,
  Visibility,
  Schedule,
  Warning,
  Business,
  Assignment,
  Science,
  Edit,
  LocationOn,
  Person,
  Timer,
  PlayArrow,
  ExpandMore,
  Add,
  Delete,
  Save,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth, UserRole } from '../contexts/AuthContext';
import samplesService, { Sample, SampleStatus, Priority } from '../services/samplesService';

interface AnalysisResult {
  element: string;
  value: number;
  unit: string;
  method: string;
  uncertainty?: number;
  detection_limit?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analysis-tabpanel-${index}`}
      aria-labelledby={`analysis-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Analysis: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [resultsDialog, setResultsDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [comments, setComments] = useState('');
  const [qualityCheck, setQualityCheck] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [newResult, setNewResult] = useState<AnalysisResult>({
    element: '',
    value: 0,
    unit: 'ppm',
    method: '',
    uncertainty: 0,
    detection_limit: 0,
  });

  // Common elements for mining analysis
  const commonElements = [
    'Au', 'Ag', 'Cu', 'Pb', 'Zn', 'Fe', 'Al', 'Ca', 'Mg', 'K', 'Na', 'Si', 'S', 'P',
    'Mn', 'Cr', 'Ni', 'Co', 'As', 'Sb', 'Bi', 'Cd', 'Hg', 'Se', 'Te', 'Mo', 'W', 'Sn'
  ];

  const analysisMethods = [
    'ICP-MS', 'ICP-OES', 'XRF', 'Essai au Feu', 'Gravimétrie', 'Titrage', 'Spectrophotométrie', 'Chromatographie'
  ];

  const units = ['ppm', 'ppb', '%', 'g/t', 'mg/kg', 'μg/g', 'oz/t'];

  useEffect(() => {
    loadAnalysisSamples();
  }, []);

  const loadAnalysisSamples = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await samplesService.getSamples(1, 100, {
        status: [SampleStatus.ANALYSE_CHIMIQUE]
      });
      
      // Double-check that we only have samples in ANALYSE_CHIMIQUE status
      const filteredSamples = response.samples.filter(sample => 
        sample.statut === SampleStatus.ANALYSE_CHIMIQUE
      );
      
      setSamples(filteredSamples);
      
      if (filteredSamples.length !== response.samples.length) {
        console.warn('Some samples were filtered out due to incorrect status');
      }
    } catch (error) {
      console.error('Error loading analysis samples:', error);
      setError('Erreur lors du chargement des échantillons en analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAnalysis = async (sample: Sample) => {
    try {
      // Update sample status with results
      const updateData = {
        statut: SampleStatus.PRET,
        metadonnees: {
          ...sample.metadonnees,
          date_fin_analyse: new Date().toISOString(),
          resultats_analyse: analysisResults,
          commentaires_analyse: comments,
          controle_qualite_effectue: qualityCheck,
          analyste_final: user?.firstName + ' ' + user?.lastName,
        }
      };

      await samplesService.updateSample(sample.id, updateData);
      
      setSuccessMessage(`Analyse complétée pour l'échantillon ${sample.sample_id}`);
      setCompleteDialog(false);
      resetDialogState();
      loadAnalysisSamples();
    } catch (error) {
      console.error('Error completing analysis:', error);
      setError('Erreur lors de la finalisation de l\'analyse');
    }
  };

  const resetDialogState = () => {
    setSelectedSample(null);
    setComments('');
    setQualityCheck(false);
    setAnalysisResults([]);
    setTabValue(0);
    setNewResult({
      element: '',
      value: 0,
      unit: 'ppm',
      method: '',
      uncertainty: 0,
      detection_limit: 0,
    });
  };

  const handleAddResult = () => {
    if (newResult.element && newResult.method) {
      setAnalysisResults([...analysisResults, { ...newResult }]);
      setNewResult({
        element: '',
        value: 0,
        unit: 'ppm',
        method: '',
        uncertainty: 0,
        detection_limit: 0,
      });
    }
  };

  const handleDeleteResult = (index: number) => {
    setAnalysisResults(analysisResults.filter((_, i) => i !== index));
  };

  const handleEditSample = (sample: Sample) => {
    navigate(`/samples/${sample.id}`);
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE: return 'error';
      case Priority.ELEVEE: return 'warning';
      case Priority.NORMALE: return 'info';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE: return 'Urgente';
      case Priority.ELEVEE: return 'Élevée';
      case Priority.NORMALE: return 'Normale';
      default: return priority;
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAnalysisDuration = (sample: Sample) => {
    if (!sample.metadonnees?.date_debut_analyse) return 0;
    return Math.floor((Date.now() - new Date(sample.metadonnees.date_debut_analyse).getTime()) / (1000 * 60 * 60));
  };

  const getAnalysisProgress = (sample: Sample) => {
    // Simulate progress based on duration and method
    const duration = getAnalysisDuration(sample);
    const method = sample.metadonnees?.methode_analyse_prevue || '';
    
    let expectedDuration = 8; // default 8 hours
    if (method.includes('ICP')) expectedDuration = 4;
    if (method.includes('XRF')) expectedDuration = 2;
    if (method.includes('FIRE_ASSAY')) expectedDuration = 24;
    
    return Math.min(Math.round((duration / expectedDuration) * 100), 95);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Chargement des échantillons en analyse...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="#" onClick={() => navigate('/dashboard')}>
          Tableau de Bord
        </Link>
        <Typography color="text.primary">Analyse Chimique</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Analyse Chimique
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestion des échantillons en cours d'analyse - {samples.length} échantillons
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Assignment />}
            onClick={() => navigate('/samples')}
          >
            Tous les Échantillons
          </Button>
          <Button
            variant="outlined"
            startIcon={<Science />}
            onClick={() => navigate('/dashboard')}
          >
            Préparation
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={() => navigate('/samples?status=PRET')}
          >
            Échantillons Prêts
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total en Analyse
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {samples.length}
                  </Typography>
                  <Typography variant="caption" color="primary.main">
                    Analyses actives
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Analytics />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Urgents
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {samples.filter(s => s.priorite === Priority.URGENTE).length}
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    Priorité maximale
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Prêts à Finaliser
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {samples.filter(s => getAnalysisProgress(s) >= 90).length}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Plus de 90% complétés
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Durée Moyenne
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {samples.length > 0 
                      ? Math.round(samples.reduce((acc, s) => acc + getAnalysisDuration(s), 0) / samples.length)
                      : 0}h
                  </Typography>
                  <Typography variant="caption" color="info.main">
                    Temps d'analyse
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <Timer />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Samples Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Échantillons en Analyse ({samples.length})
          </Typography>
          
          {samples.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Analytics sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucun échantillon en analyse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tous les échantillons ont été analysés ou sont en cours de préparation.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Science />}
                onClick={() => navigate('/dashboard')}
                sx={{ mt: 2 }}
              >
                Voir la Préparation
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Échantillon</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Analyste</TableCell>
                    <TableCell>Méthode</TableCell>
                    <TableCell>Priorité</TableCell>
                    <TableCell>Progression</TableCell>
                    <TableCell>Durée</TableCell>
                    <TableCell>Localisation</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {samples.map((sample) => {
                    const duration = getAnalysisDuration(sample);
                    const progress = getAnalysisProgress(sample);
                    
                    return (
                      <TableRow key={sample.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                              {sample.sample_id.split('-')[2]?.slice(-2) || 'XX'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {sample.sample_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sample.external_id || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">
                                {sample.client?.company_name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sample.nom_projet || ''}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">
                                {sample.metadonnees?.analyste_assigne || 'Non assigné'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Analyse Chimique
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Analytics sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {sample.metadonnees?.methode_analyse_prevue || 'Standard'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPriorityLabel(sample.priorite)}
                            size="small"
                            color={getPriorityColor(sample.priorite)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={progress} 
                                sx={{ height: 8, borderRadius: 4 }}
                                color={progress >= 90 ? 'success' : progress >= 70 ? 'warning' : 'secondary'}
                              />
                            </Box>
                            <Typography variant="body2" fontWeight="medium">
                              {progress}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Timer sx={{ 
                              fontSize: 16, 
                              color: duration > 48 ? 'warning.main' : 'text.secondary' 
                            }} />
                            <Typography 
                              variant="body2"
                              color={duration > 48 ? 'warning.main' : 'text.primary'}
                              fontWeight={duration > 48 ? 'bold' : 'normal'}
                            >
                              {duration}h
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {sample.localisation_stockage || 'Laboratoire Analyse'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/samples/${sample.id}`)}
                              title="Voir détails"
                            >
                              <Visibility />
                            </IconButton>
                            {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST]) && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditSample(sample)}
                                title="Modifier échantillon"
                              >
                                <Edit />
                              </IconButton>
                            )}
                            {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST]) && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setSelectedSample(sample);
                                  setAnalysisResults(sample.metadonnees?.resultats_analyse || []);
                                  setResultsDialog(true);
                                }}
                                title="Saisir résultats"
                              >
                                <Edit />
                              </IconButton>
                            )}
                            {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST, UserRole.QUALITY_MANAGER]) && (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => {
                                  setSelectedSample(sample);
                                  setAnalysisResults(sample.metadonnees?.resultats_analyse || []);
                                  setCompleteDialog(true);
                                }}
                                title="Finaliser analyse"
                                disabled={progress < 90}
                              >
                                <CheckCircle />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Results Entry Dialog */}
      <Dialog open={resultsDialog} onClose={() => setResultsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Analytics color="primary" />
            Saisie des Résultats d'Analyse
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSample && (
            <>
              {/* Sample Information */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedSample.sample_id} - {selectedSample.client?.company_name}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">Méthode d'Analyse</Typography>
                      <Typography variant="body1">{selectedSample.metadonnees?.methode_analyse_prevue}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">Analyste</Typography>
                      <Typography variant="body1">{selectedSample.metadonnees?.analyste_assigne}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">Durée d'Analyse</Typography>
                      <Typography variant="body1">{getAnalysisDuration(selectedSample)} heures</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Add New Result */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Ajouter un Résultat</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Élément</InputLabel>
                        <Select
                          value={newResult.element}
                          onChange={(e) => setNewResult({...newResult, element: e.target.value})}
                          label="Élément"
                        >
                          {commonElements.map((element) => (
                            <MenuItem key={element} value={element}>{element}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Valeur"
                        type="number"
                        value={newResult.value}
                        onChange={(e) => setNewResult({...newResult, value: parseFloat(e.target.value) || 0})}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth>
                        <InputLabel>Unité</InputLabel>
                        <Select
                          value={newResult.unit}
                          onChange={(e) => setNewResult({...newResult, unit: e.target.value})}
                          label="Unité"
                        >
                          {units.map((unit) => (
                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Méthode</InputLabel>
                        <Select
                          value={newResult.method}
                          onChange={(e) => setNewResult({...newResult, method: e.target.value})}
                          label="Méthode"
                        >
                          {analysisMethods.map((method) => (
                            <MenuItem key={method} value={method}>{method}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddResult}
                        disabled={!newResult.element || !newResult.method}
                      >
                        Ajouter
                      </Button>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Results Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Élément</TableCell>
                      <TableCell>Valeur</TableCell>
                      <TableCell>Unité</TableCell>
                      <TableCell>Méthode</TableCell>
                      <TableCell>Incertitude</TableCell>
                      <TableCell>Limite Détection</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysisResults.map((result, index) => (
                      <TableRow key={index}>
                                                 <TableCell>
                           <Typography variant="body2" fontWeight="medium">{result.element}</Typography>
                         </TableCell>
                        <TableCell>{result.value}</TableCell>
                        <TableCell>{result.unit}</TableCell>
                        <TableCell>{result.method}</TableCell>
                        <TableCell>{result.uncertainty || '-'}</TableCell>
                        <TableCell>{result.detection_limit || '-'}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteResult(index)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setResultsDialog(false)}>
            Fermer
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={() => {
              // Save results to sample metadata
              if (selectedSample) {
                const updateData = {
                  metadonnees: {
                    ...selectedSample.metadonnees,
                    resultats_analyse: analysisResults,
                  }
                };
                samplesService.updateSample(selectedSample.id, updateData);
                setSuccessMessage('Résultats sauvegardés avec succès');
                setResultsDialog(false);
              }
            }}
            disabled={analysisResults.length === 0}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Analysis Dialog */}
      <Dialog open={completeDialog} onClose={() => setCompleteDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircle color="success" />
            Finaliser l'Analyse
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSample && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Vous êtes sur le point de finaliser l'analyse de l'échantillon <strong>{selectedSample.sample_id}</strong>.
                Cette action marquera l'échantillon comme prêt et générera le rapport final.
              </Alert>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Résumé de l'Analyse
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Nombre de Résultats</Typography>
                      <Typography variant="body1" fontWeight="medium">{analysisResults.length} éléments analysés</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Durée Totale</Typography>
                      <Typography variant="body1">{getAnalysisDuration(selectedSample)} heures</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Méthode Utilisée</Typography>
                      <Typography variant="body1">{selectedSample.metadonnees?.methode_analyse_prevue}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Analyste</Typography>
                      <Typography variant="body1">{selectedSample.metadonnees?.analyste_assigne}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={qualityCheck}
                    onChange={(e) => setQualityCheck(e.target.checked)}
                  />
                }
                label="Contrôle qualité effectué et validé"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Commentaires Finaux"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Ajouter des commentaires sur l'analyse, observations particulières, recommandations..."
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => {
            setCompleteDialog(false);
            resetDialogState();
          }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => selectedSample && handleCompleteAnalysis(selectedSample)}
            startIcon={<CheckCircle />}
            disabled={!qualityCheck || analysisResults.length === 0}
            size="large"
          >
            Finaliser l'Analyse
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Analysis; 